import tokens from '@timelyapp/design-system/tokens.json';
import type { DropdownMenuItem } from '@timelyapp/react';
import { Button, DropdownMenu, Icon, Table } from '@timelyapp/react';
import classNames from 'classnames';
import { SortAsc, SortDesc } from 'components/Icons';
import React, { type ReactNode, useCallback, useMemo, useState } from 'react';
import styles from './column.module.scss';
import type { DatatableHeaderRenderer } from './types';

const sortLabels = {
  number: { asc: 'Sort 1 \u2192 9', desc: 'Sort 9 \u2192 1' },
  string: { asc: 'Sort A \u2192 Z', desc: 'Sort Z \u2192 A' },
  date: { asc: 'Sort Earliest \u2192 Latest', desc: 'Sort Latest \u2192 Earliest' },
} as const;

interface Props {
  label?: string | ReactNode;
  /** Display labels based on what type of data we sort (e.g: A → Z, 0 → 9) */
  sortLabelType?: keyof typeof sortLabels;
  /**
   * CSS selector for the related filter element (if there is one)
   * We use this temporary solution to open the filter when user choses this action in the menu
   */
  filterTriggerSelector?: string;
  alignRight?: boolean;
}

const HeaderWithMenu: DatatableHeaderRenderer<Props> = ({
  header,
  table,
  label = '',
  sortLabelType = 'string',
  filterTriggerSelector,
  alignRight,
}) => {
  const isPinned = header.column.getIsPinned();
  const deltaOffset = table.getState().columnSizingInfo.deltaOffset;

  const [isOpen, setIsOpen] = useState(false);
  const handleThClick = useCallback((event: React.MouseEvent<HTMLTableCellElement>) => {
    if (event.target === event.currentTarget) {
      setIsOpen(true);
    }
  }, []);

  const sorted = header.column.getIsSorted();

  const menuItems = useMemo(() => {
    const { getCanSort, getCanHide } = header.column;
    const items: Array<DropdownMenuItem> = [];

    if (getCanSort()) {
      items.push(
        {
          type: 'item',
          label: sortLabels[sortLabelType].asc,
          icon: !sorted ? (
            <SortAsc />
          ) : sorted === 'asc' ? (
            <Icon name="Check" type="solid" color={tokens['theme_color_solid_primary']} />
          ) : undefined,
          onSelect: () => header.column.toggleSorting(false),
        },
        {
          type: 'item',
          label: sortLabels[sortLabelType].desc,
          icon: !sorted ? (
            <SortDesc />
          ) : sorted === 'desc' ? (
            <Icon name="Check" type="solid" color={tokens['theme_color_solid_primary']} />
          ) : undefined,
          onSelect: () => header.column.toggleSorting(true),
        },
        { type: 'separator' }
      );
    }

    if (getCanHide()) {
      const isVisible = header.column.getIsVisible();
      const parentColumnId = (
        header.column.columnDef.meta as { parentColumnId?: string } | undefined
      )?.parentColumnId;
      items.push({
        type: 'item',
        label: isVisible ? 'Hide column' : 'Show column',
        icon: <Icon name={isVisible ? 'EyeOff' : 'Eye'} />,
        onSelect: () => {
          // If this column has a parent column, toggle the parent's visibility instead
          if (parentColumnId) {
            table.setColumnVisibility(prev => ({
              ...prev,
              [parentColumnId]: !isVisible,
            }));
            return;
          }
          header.column.toggleVisibility();
        },
      });
    }

    if (filterTriggerSelector) {
      items.push({
        type: 'item',
        label: 'Filter',
        icon: <Icon name="FilterFunnel01" />,
        onSelect: () => {
          // Hacky workaround to jump to filter
          document.querySelector<HTMLButtonElement>(filterTriggerSelector)?.click();
        },
      });
    }

    return items;
  }, [header.column, filterTriggerSelector, sortLabelType, sorted, table]);

  return (
    <Table.Th
      key={header.id}
      sortable={header.column.getCanSort()}
      sorted={!!sorted}
      pinnedLeft={isPinned === 'left'}
      pinnedRight={isPinned === 'right'}
      className={classNames(styles.header, { [styles.alignRight]: alignRight })}
      data-testid={`${header.id}_column_header`}
      {...{
        colSpan: header.colSpan,
        style: {
          width: header.getSize(),
        },
      }}
    >
      <div onClick={handleThClick}>
        {header.isPlaceholder ? null : menuItems.length ? (
          <DropdownMenu
            open={isOpen}
            closeOnSelect
            onOpenChange={open => setIsOpen(open)}
            align="start"
            trigger={
              <Button size="small" priority="low" className={styles.menuTrigger}>
                {label}
                {sorted && (
                  <Icon
                    name={sorted === 'asc' ? 'ArrowNarrowDown' : 'ArrowNarrowUp'}
                    type="solid"
                    className={styles.sortIcon}
                  />
                )}
              </Button>
            }
            items={menuItems}
          />
        ) : (
          <span>{label}</span>
        )}
      </div>
      {header.column.getCanResize() && (
        <Table.ThResizer
          header={header}
          // columnResizeMode={columnResizeMode}
          deltaOffset={deltaOffset}
        />
      )}
    </Table.Th>
  );
};

export default HeaderWithMenu;
